import React, { useState, useEffect, useCallback } from 'react';
import { fetchDeliveryOntime, fetchUpsTracking } from '../services/api';
import { ChevronLeft, ChevronRight, Clock, CheckCircle, AlertTriangle, Truck, Package, X } from 'lucide-react';

// ─── Date helpers ────────────────────────────────────────────────────────────

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
}

function toIso(d) { return d.toISOString().split('T')[0]; }
function getSunday(monday) { const s = new Date(monday); s.setDate(s.getDate() + 6); return s; }

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateFull(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatWeekLabel(s, e) { return `${formatDate(s)} – ${formatDate(e)}`; }

function fmtUpsDate(dateStr, timeStr) {
  if (!dateStr) return '—';
  const y = dateStr.substring(0, 4), m = dateStr.substring(4, 6), d = dateStr.substring(6, 8);
  let out = `${m}/${d}/${y}`;
  if (timeStr) {
    const hh = parseInt(timeStr.substring(0, 2)), mm = timeStr.substring(2, 4);
    const ampm = hh >= 12 ? 'PM' : 'AM';
    out += ` ${hh > 12 ? hh - 12 : hh || 12}:${mm} ${ampm}`;
  }
  return out;
}

const fmt = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLE = {
  on_time: { bg: '#e6f4ea', color: '#1a8a4a', label: 'On Time' },
  late: { bg: '#fde8e8', color: '#c62828', label: 'Late' },
  overdue: { bg: '#fde8e8', color: '#c62828', label: 'Overdue' },
  in_transit: { bg: '#e3f2fd', color: '#1565c0', label: 'In Transit' },
};

// Small brown UPS shield icon
function UpsShield({ size = 16, style }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} style={{ verticalAlign: 'middle', ...style }}>
      <path d="M12 2C8.5 2 5 3 5 3v10c0 5.5 7 9 7 9s7-3.5 7-9V3s-3.5-1-7-1z" fill="#351C15"/>
      <path d="M12 4C9.5 4 7 4.7 7 4.7v8.3c0 4 5 6.5 5 6.5s5-2.5 5-6.5V4.7S14.5 4 12 4z" fill="#FFB500"/>
    </svg>
  );
}

// ─── Tracking Overlay ────────────────────────────────────────────────────────

function TrackingOverlay({ trackingNumber, shipMethod, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!trackingNumber) { setLoading(false); setError('No tracking number'); return; }
    fetchUpsTracking(trackingNumber)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [trackingNumber]);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)' }} />
      <div style={{ position: 'relative', width: 400, maxHeight: '80vh', overflow: 'auto', background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', padding: 0 }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--gray-100)', position: 'sticky', top: 0, background: '#fff', borderRadius: '10px 10px 0 0', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UpsShield size={20} />
            <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13 }}>UPS Tracking</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--gray-400)' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '14px 16px', fontSize: 12 }}>
          {loading && <div style={{ color: 'var(--gray-500)', textAlign: 'center', padding: 20 }}>Loading tracking data...</div>}
          {error && <div style={{ color: 'var(--red)', padding: 8 }}>Error: {error}</div>}
          {data && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--gray-600)' }}>{data.trackingNumber}</span>
                <span style={{ fontWeight: 700, fontSize: 12, color: data.statusCode === '011' ? '#1a8a4a' : 'var(--orange)' }}>{data.status}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginBottom: 14 }}>
                {data.service && <><span style={{ color: 'var(--gray-500)' }}>Service</span><span style={{ fontWeight: 600 }}>{data.service}</span></>}
                {data.weight && <><span style={{ color: 'var(--gray-500)' }}>Weight</span><span>{data.weight}</span></>}
                {data.scheduledDelivery && <><span style={{ color: 'var(--gray-500)' }}>Scheduled</span><span>{fmtUpsDate(data.scheduledDelivery)}</span></>}
                {data.actualDelivery && <><span style={{ color: 'var(--gray-500)' }}>Delivered</span><span style={{ color: '#1a8a4a', fontWeight: 600 }}>{fmtUpsDate(data.actualDelivery)}</span></>}
              </div>

              {(data.podImage || data.podCaptured) && (
                <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 10, marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6, fontSize: 11 }}>Delivery Photo</div>
                  {data.podImage
                    ? <img src={data.podImage} alt="Delivery" style={{ width: '100%', borderRadius: 6, border: '1px solid var(--gray-200)' }} />
                    : <div style={{ fontSize: 11, color: 'var(--gray-500)', fontStyle: 'italic' }}>Photo on file — view at ups.com</div>}
                </div>
              )}

              {data.activities?.length > 0 && (
                <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 10 }}>
                  <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 8, fontSize: 11 }}>Activity</div>
                  {data.activities.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6, alignItems: 'flex-start' }}>
                      <span style={{ color: 'var(--gray-400)', whiteSpace: 'nowrap', minWidth: 110, fontSize: 11 }}>{fmtUpsDate(a.date, a.time)}</span>
                      <span style={{ color: i === 0 ? 'var(--navy)' : 'var(--gray-600)', fontWeight: i === 0 ? 600 : 400, fontSize: 11 }}>
                        {a.description}{a.location ? ` · ${a.location}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function DeliveryReconciliation({ navigateTo }) {
  const today = new Date();
  const currentMonday = getMonday(today);
  // Show previous week until Tuesday (day 2), then roll to current week
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, 2=Tue
  const defaultMonday = (dayOfWeek === 0 || dayOfWeek === 1)
    ? (() => { const d = new Date(currentMonday); d.setDate(d.getDate() - 7); return d; })()
    : currentMonday;

  const [mode, setMode] = useState('week');
  const [weekStart, setWeekStart] = useState(toIso(defaultMonday));
  const [weekEnd, setWeekEnd] = useState(toIso(getSunday(defaultMonday)));
  const [customStart, setCustomStart] = useState(toIso(currentMonday));
  const [customEnd, setCustomEnd] = useState(toIso(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMethod, setExpandedMethod] = useState(null);
  const [trackingOverlay, setTrackingOverlay] = useState(null); // { trackingNumber, shipMethod }

  const startDate = mode === 'week' ? weekStart : customStart;
  const endDate = mode === 'week' ? weekEnd : customEnd;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setExpandedMethod(null);
    fetchDeliveryOntime(startDate, endDate)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const shiftWeek = (direction) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + (direction * 7));
    setWeekStart(toIso(d));
    setWeekEnd(toIso(getSunday(d)));
  };

  const isCurrentWeek = weekStart === toIso(currentMonday) || new Date(weekEnd) > today;

  const summary = data?.summary || [];
  const allOrders = data?.allOrders || [];
  const lateOrders = data?.lateOrders || [];

  const totals = summary.reduce((acc, row) => ({
    allOrders: acc.allOrders + Number(row.total_all || 0),
    shipped: acc.shipped + Number(row.total_orders || 0),
    onTime: acc.onTime + Number(row.on_time || 0),
    late: acc.late + Number(row.late || 0),
    inTransit: acc.inTransit + Number(row.in_transit || 0),
    delivered: acc.delivered + Number(row.delivered || 0),
    handCarry: acc.handCarry + Number(row.hand_carry || 0),
  }), { allOrders: 0, shipped: 0, onTime: 0, late: 0, inTransit: 0, delivered: 0, handCarry: 0 });

  const onTimePct = totals.delivered > 0 ? ((totals.onTime / totals.delivered) * 100).toFixed(1) : '—';
  const lateOrderValue = lateOrders.reduce((s, o) => s + Number(o.order_amount || 0), 0);

  const pctColor = (pct) => {
    if (pct === '—') return 'var(--gray-400)';
    const n = parseFloat(pct);
    if (n >= 95) return '#1a8a4a';
    if (n >= 85) return '#b06a00';
    return 'var(--red)';
  };

  const ordersByMethod = {};
  allOrders.forEach(o => {
    if (!ordersByMethod[o.ship_method]) ordersByMethod[o.ship_method] = [];
    ordersByMethod[o.ship_method].push(o);
  });

  const openTracking = (trackingNumber, shipMethod) => {
    if (trackingNumber) setTrackingOverlay({ trackingNumber, shipMethod });
  };

  return (
    <div className="page-content">
      {/* Tracking Overlay */}
      {trackingOverlay && (
        <TrackingOverlay
          trackingNumber={trackingOverlay.trackingNumber}
          shipMethod={trackingOverlay.shipMethod}
          onClose={() => setTrackingOverlay(null)}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--navy)' }}>On-Time Delivery</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: 13 }}>
            Scheduled vs actual delivery performance by ship method
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--gray-200)' }}>
            {[['week', 'Weekly'], ['custom', 'Custom Range']].map(([m, label]) => (
              <button key={m} onClick={() => setMode(m)}
                style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: mode === m ? 'var(--navy)' : '#fff', color: mode === m ? '#fff' : 'var(--gray-600)' }}>
                {label}
              </button>
            ))}
          </div>
          {mode === 'week' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => shiftWeek(-1)} className="btn btn-ghost" style={{ padding: '4px 8px' }}><ChevronLeft size={16} /></button>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', minWidth: 160, textAlign: 'center' }}>
                {formatWeekLabel(weekStart, weekEnd)}
                {isCurrentWeek && <span style={{ fontSize: 10, color: 'var(--gray-400)', marginLeft: 6 }}>(This Week)</span>}
              </span>
              <button onClick={() => shiftWeek(1)} className="btn btn-ghost"
                style={{ padding: '4px 8px', opacity: isCurrentWeek ? 0.3 : 1 }} disabled={isCurrentWeek}>
                <ChevronRight size={16} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 2 }}>FROM</div>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 2 }}>TO</div>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
              </div>
              <button onClick={load} className="btn btn-primary" style={{ fontSize: 11, marginTop: 14 }}>Run</button>
            </div>
          )}
        </div>
      </div>

      {loading && <div className="card" style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>Loading delivery data...</div>}
      {error && <div className="card" style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}

      {!loading && !error && (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'On-Time Rate', value: onTimePct === '—' ? '—' : `${onTimePct}%`, icon: CheckCircle, color: pctColor(onTimePct), sub: `${totals.onTime} of ${totals.delivered} delivered` },
              { label: 'Late Deliveries', value: totals.late, icon: AlertTriangle, color: totals.late > 0 ? 'var(--red)' : '#1a8a4a', sub: totals.late > 0 ? fmt(lateOrderValue) + ' order value' : 'No late deliveries' },
              { label: 'In Transit', value: totals.inTransit, icon: Truck, color: 'var(--navy)', sub: totals.handCarry > 0 ? `+ ${totals.handCarry} hand carry` : 'Awaiting delivery confirmation' },
              { label: 'Total Orders', value: totals.allOrders, icon: Package, color: 'var(--navy)', sub: `${totals.shipped} shipped, ${totals.delivered} delivered` },
            ].map(({ label, value, icon: Icon, color, sub }) => (
              <div key={label} className="card" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <Icon size={14} style={{ color: 'var(--gray-400)' }} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>{sub}</div>
              </div>
            ))}
          </div>

          {/* Ship Method Breakdown */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>By Ship Method</span>
              {expandedMethod && (
                <button onClick={() => setExpandedMethod(null)} className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }}>Collapse</button>
              )}
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {summary.filter(row => Number(row.total_orders || 0) > 0).length === 0 ? (
                <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No orders with delivery data found for this period.</div>
              ) : (
                <table className="product-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Ship Method</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th style={{ textAlign: 'right' }}>On Time</th>
                      <th style={{ textAlign: 'right' }}>Late</th>
                      <th style={{ textAlign: 'right' }}>In Transit</th>
                      <th style={{ textAlign: 'right' }}>On-Time %</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.filter(row => Number(row.total_orders || 0) > 0).map(row => {
                      const total = Number(row.total_orders || 0);
                      const onT = Number(row.on_time || 0);
                      const lt = Number(row.late || 0);
                      const transit = Number(row.in_transit || 0);
                      const delivered = Number(row.delivered || 0);
                      const pct = delivered > 0 ? ((onT / delivered) * 100).toFixed(1) : '—';
                      const isExpanded = expandedMethod === row.ship_method;
                      const methodOrders = ordersByMethod[row.ship_method] || [];

                      return (
                        <React.Fragment key={row.ship_method}>
                          <tr style={{ background: isExpanded ? 'var(--gray-50)' : undefined, cursor: total > 0 ? 'pointer' : 'default' }}
                            onClick={() => total > 0 && setExpandedMethod(isExpanded ? null : row.ship_method)}>
                            <td style={{ fontWeight: 600 }}>{row.ship_method}</td>
                            <td style={{ textAlign: 'right' }}>{total}</td>
                            <td style={{ textAlign: 'right', color: '#1a8a4a' }}>{onT}</td>
                            <td style={{ textAlign: 'right', color: lt > 0 ? 'var(--red)' : 'var(--gray-400)', fontWeight: lt > 0 ? 600 : 400 }}>{lt}</td>
                            <td style={{ textAlign: 'right', color: 'var(--gray-500)' }}>{transit}</td>
                            <td style={{ textAlign: 'right' }}>
                              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                                background: pct === '—' ? 'var(--gray-100)' : parseFloat(pct) >= 95 ? '#e6f4ea' : parseFloat(pct) >= 85 ? '#fff8e1' : '#fde8e8',
                                color: pctColor(pct) }}>
                                {pct === '—' ? '—' : `${pct}%`}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center', width: 30 }}>
                              {total > 0 && <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>{isExpanded ? '▾' : '▸'}</span>}
                            </td>
                          </tr>
                          {isExpanded && methodOrders.length > 0 && (
                            <tr>
                              <td colSpan={7} style={{ padding: 0, background: 'var(--gray-50)' }}>
                                <table style={{ width: '100%', fontSize: 11 }}>
                                  <thead>
                                    <tr style={{ background: 'var(--gray-100)' }}>
                                      <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)' }}>Order</th>
                                      <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)' }}>Customer</th>
                                      <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)' }}>Amount</th>
                                      <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)' }}>Shipped</th>
                                      <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)' }}>Expected</th>
                                      <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)' }}>Delivered</th>
                                      <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)' }}>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {methodOrders.map((order, i) => {
                                      const st = STATUS_STYLE[order.status] || STATUS_STYLE.in_transit;
                                      return (
                                        <tr key={order.orderid || i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                                          <td style={{ padding: '5px 12px' }}>
                                            <span onClick={() => navigateTo?.('order-details', order.bc_order_id || order.orderid)}
                                              style={{ fontWeight: 600, color: 'var(--navy)', cursor: 'pointer' }}>
                                              #{order.bc_order_id || order.orderid}
                                            </span>
                                          </td>
                                          <td style={{ padding: '5px 12px', color: 'var(--gray-600)' }}>{order.customer_name}</td>
                                          <td style={{ padding: '5px 12px', textAlign: 'right' }}>{fmt(order.order_amount)}</td>
                                          <td style={{ padding: '5px 12px', color: 'var(--gray-600)' }}>{formatDateFull(order.ship_date)}</td>
                                          <td style={{ padding: '5px 12px', color: 'var(--gray-600)' }}>{formatDateFull(order.estimated_delivery)}</td>
                                          <td style={{ padding: '5px 12px', color: order.status === 'late' || order.status === 'overdue' ? 'var(--red)' : 'var(--gray-600)' }}>
                                            {order.status === 'overdue' ? 'Overdue' : formatDateFull(order.actual_delivery)}
                                          </td>
                                          <td style={{ padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: st.bg, color: st.color }}>
                                              {st.label}{order.days_late > 0 ? ` +${order.days_late}d` : ''}
                                            </span>
                                            {order.tracking_number && (
                                              <span onClick={e => { e.stopPropagation(); openTracking(order.tracking_number, order.ship_method); }}
                                                style={{ cursor: 'pointer', opacity: 0.7, display: 'inline-flex' }}
                                                title={`Track ${order.tracking_number}`}>
                                                <UpsShield size={14} />
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <tr style={{ borderTop: '2px solid var(--gray-200)', fontWeight: 700, background: 'var(--gray-50)' }}>
                      <td>Total</td>
                      <td style={{ textAlign: 'right' }}>{totals.shipped}</td>
                      <td style={{ textAlign: 'right', color: '#1a8a4a' }}>{totals.onTime}</td>
                      <td style={{ textAlign: 'right', color: totals.late > 0 ? 'var(--red)' : 'var(--gray-400)' }}>{totals.late}</td>
                      <td style={{ textAlign: 'right', color: 'var(--gray-500)' }}>{totals.inTransit}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                          background: onTimePct === '—' ? 'var(--gray-100)' : parseFloat(onTimePct) >= 95 ? '#e6f4ea' : parseFloat(onTimePct) >= 85 ? '#fff8e1' : '#fde8e8',
                          color: pctColor(onTimePct) }}>
                          {onTimePct === '—' ? '—' : `${onTimePct}%`}
                        </span>
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Late Orders Detail */}
          {lateOrders.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <Clock size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Late Deliveries ({lateOrders.length})
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                <table className="product-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Order</th>
                      <th style={{ textAlign: 'left' }}>Customer</th>
                      <th style={{ textAlign: 'left' }}>Ship Method</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th style={{ textAlign: 'left' }}>Expected</th>
                      <th style={{ textAlign: 'left' }}>Delivered</th>
                      <th style={{ textAlign: 'right' }}>Days Late</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lateOrders.map((order, i) => {
                      const daysLate = Number(order.days_late || 0);
                      const isOverdue = order.is_overdue;
                      return (
                        <tr key={order.orderid || i} style={{ background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                          <td>
                            <span onClick={() => navigateTo?.('order-details', order.bc_order_id || order.orderid)}
                              style={{ fontWeight: 600, color: 'var(--navy)', cursor: 'pointer' }}>
                              #{order.bc_order_id || order.orderid}
                            </span>
                          </td>
                          <td>{order.customer_name}</td>
                          <td style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {order.ship_method}
                            {order.tracking_number && (
                              <span onClick={() => openTracking(order.tracking_number, order.ship_method)}
                                style={{ cursor: 'pointer', opacity: 0.7, display: 'inline-flex' }}
                                title={`Track ${order.tracking_number}`}>
                                <UpsShield size={14} />
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>{fmt(order.order_amount)}</td>
                          <td>{formatDateFull(order.estimated_delivery)}</td>
                          <td style={{ color: isOverdue ? 'var(--red)' : undefined, fontWeight: isOverdue ? 600 : 400 }}>
                            {isOverdue ? 'Overdue' : formatDateFull(order.actual_delivery)}
                          </td>
                          <td style={{ textAlign: 'right', color: 'var(--red)', fontWeight: 600 }}>+{daysLate}d</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {totals.late === 0 && totals.delivered > 0 && (
            <div className="card" style={{ padding: 24, textAlign: 'center' }}>
              <CheckCircle size={32} style={{ color: '#1a8a4a', marginBottom: 8 }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a8a4a' }}>100% On-Time Delivery</div>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>All {totals.delivered} delivered orders arrived on schedule</div>
            </div>
          )}

          {totals.allOrders === 0 && (
            <div className="card" style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--gray-500)' }}>
              No orders with delivery tracking data in this period. Delivery dates are synced nightly — data may take up to 24 hours to appear for recent orders.
            </div>
          )}
        </>
      )}
    </div>
  );
}
